import { useEffect, useMemo, useState } from "react";

import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  PropertyAddressProviderError,
  getPropertyAddressProvider,
  type PropertyAddressSelection,
  type PropertyAddressSuggestion,
} from "./property-address-provider";

export function PropertyAddressAutocompleteField({
  manualOverrideExpanded,
  onManualOverrideChange,
  onSelectAddress,
  onValueChange,
  selectedAddress,
  value,
}: {
  manualOverrideExpanded: boolean;
  onManualOverrideChange: (expanded: boolean) => void;
  onSelectAddress: (selection: PropertyAddressSelection) => void;
  onValueChange: (value: string) => void;
  selectedAddress: PropertyAddressSelection | null;
  value: string;
}) {
  const provider = useMemo(() => getPropertyAddressProvider(), []);
  const [activeSuggestionID, setActiveSuggestionID] = useState<string | null>(
    null,
  );
  const [lookupState, setLookupState] = useState<{
    message: string | null;
    query: string;
    suggestions: PropertyAddressSuggestion[];
  }>({
    message: null,
    query: "",
    suggestions: [],
  });

  const query = value.trim();
  const hasSelectedAddress =
    !!selectedAddress && query === selectedAddress.addressLine1;
  const shouldSearch = Boolean(
    provider && query.length >= 3 && !hasSelectedAddress,
  );
  const searching = shouldSearch && lookupState.query !== query;
  const suggestions =
    shouldSearch && lookupState.query === query ? lookupState.suggestions : [];
  const message = buildMessage({
    providerConfigured: !!provider,
    query,
    stateMessage:
      shouldSearch && lookupState.query === query ? lookupState.message : null,
  });

  useEffect(() => {
    if (!provider || !shouldSearch) {
      return;
    }

    let cancelled = false;

    void provider
      .search(query)
      .then((nextSuggestions) => {
        if (cancelled) {
          return;
        }
        if (nextSuggestions.length === 0) {
          onManualOverrideChange(true);
        }
        setLookupState({
          message:
            nextSuggestions.length > 0
              ? null
              : "No address matches yet. Keep typing or enter the address manually.",
          query,
          suggestions: nextSuggestions,
        });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        onManualOverrideChange(true);
        setLookupState({
          message: buildProviderMessage(error),
          query,
          suggestions: [],
        });
      });

    return () => {
      cancelled = true;
    };
  }, [onManualOverrideChange, provider, query, shouldSearch]);

  const handleSelectSuggestion = async (
    suggestion: PropertyAddressSuggestion,
  ) => {
    if (!provider) {
      return;
    }
    setActiveSuggestionID(suggestion.id);
    try {
      const selection = await provider.select(suggestion);
      setLookupState({
        message: null,
        query: suggestion.mainText,
        suggestions: [],
      });
      onSelectAddress(selection);
    } catch (error) {
      setLookupState({
        message: buildProviderMessage(error),
        query,
        suggestions: [],
      });
    } finally {
      setActiveSuggestionID(null);
    }
  };

  return (
    <div className="space-y-2">
      <Input
        aria-autocomplete={provider ? "list" : "none"}
        aria-controls="property-address-suggestions"
        aria-expanded={suggestions.length > 0}
        description={message}
        id="property-address-line-1"
        label="Address"
        onChange={(event) => {
          const nextValue = event.target.value;
          onValueChange(nextValue);
          if (!provider && nextValue.trim().length >= 3) {
            onManualOverrideChange(true);
          }
        }}
        required
        value={value}
      />
      {suggestions.length > 0 ? (
        <div
          className="surface-card space-y-1 border border-border/70 bg-card p-2"
          id="property-address-suggestions"
          role="listbox"
        >
          {suggestions.map((suggestion) => {
            const isActive = suggestion.id === activeSuggestionID;
            return (
              <button
                className={cn(
                  "w-full rounded-xl px-3 py-2 text-left transition-colors",
                  "hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  isActive && "cursor-wait opacity-70",
                )}
                disabled={isActive || searching}
                key={suggestion.id}
                onClick={() => void handleSelectSuggestion(suggestion)}
                role="option"
                type="button"
              >
                <span className="block text-sm font-medium text-foreground">
                  {suggestion.mainText}
                </span>
                {suggestion.secondaryText ? (
                  <span className="block text-sm text-muted-foreground">
                    {suggestion.secondaryText}
                  </span>
                ) : (
                  <span className="block text-sm text-muted-foreground">
                    {suggestion.label}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-3">
        <Button
          className="-ml-3 px-3"
          onClick={() => onManualOverrideChange(!manualOverrideExpanded)}
          size="sm"
          variant="ghost"
        >
          {manualOverrideExpanded
            ? "Hide manual address fields"
            : hasSelectedAddress
              ? "Edit address details"
              : "Enter address manually"}
        </Button>
      </div>
      <p aria-live="polite" className="sr-only">
        {message ?? ""}
      </p>
    </div>
  );
}

function buildProviderMessage(error: unknown) {
  if (error instanceof PropertyAddressProviderError) {
    if (error.code === "configuration") {
      return "Address lookup needs Places API (New) enabled and a browser-allowed API key. You can keep entering the address manually.";
    }
    if (error.code === "quota") {
      return "Address lookup hit a quota or billing limit. You can keep entering the address manually.";
    }
    return "Address lookup is unavailable right now. You can keep entering the address manually.";
  }
  return "Address lookup is unavailable right now. You can keep entering the address manually.";
}

function buildMessage({
  providerConfigured,
  query,
  stateMessage,
}: {
  providerConfigured: boolean;
  query: string;
  stateMessage: string | null;
}) {
  if (stateMessage) {
    return stateMessage;
  }
  if (!providerConfigured && query.length >= 3) {
    return "Address lookup is unavailable here. Enter the address manually below.";
  }
  return null;
}
